import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { FiMapPin, FiCalendar, FiUsers, FiMessageSquare } from 'react-icons/fi';
import { PropertyThumbnail } from '../components/OptimizedImage';
import './BookingDetail.css';

const BookingDetail = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBookingDetails();
  }, [id]);

  const fetchBookingDetails = async () => {
    try {
      const response = await api.get(`/api/bookings/${id}`);
      setBooking(response.data.booking);
    } catch (error) {
      console.error('Error fetching booking:', error);
      toast.error(t('booking.bookingNotFound'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!window.confirm(t('booking.cancelConfirm'))) return;

    try {
      await api.post(`/api/bookings/${id}/cancel`, {
        reason: 'Cancelled by user'
      });
      toast.success(t('booking.bookingCancelled'));
      fetchBookingDetails();
    } catch (error) {
      toast.error(t('booking.failedToCancel'));
    }
  };

  const handleMessageHost = () => {
    if (booking?.property?.host?.id) {
      navigate(`/messages?userId=${booking.property.host.id}`);
    }
  };

  const handleMessageGuest = () => {
    if (booking?.guest?.id) {
      navigate(`/messages?userId=${booking.guest.id}`);
    }
  };

  const isHost = user && booking?.property?.host?.id === user.id;
  const isGuest = user && booking?.guest_id === user.id;

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="page container">
        <h2>{t('booking.bookingNotFound')}</h2>
      </div>
    );
  }

  return (
    <div className="booking-detail-page">
      <div className="container">
        <h1>{t('booking.bookingDetails')}</h1>

        <div className="booking-detail-content">
          <div className="booking-main">
            {/* Status */}
            <div className="status-section">
              <span className={`status-badge badge badge-${
                booking.status === 'confirmed' ? 'success' :
                booking.status === 'pending' ? 'warning' :
                booking.status === 'cancelled' ? 'danger' : 'info'
              }`}>
                {t(`booking.${booking.status}`)}
              </span>
              <span className={`status-badge badge badge-${
                booking.payment_status === 'completed' ? 'success' : 'warning'
              }`}>
                {t('booking.payment')}: {booking.payment_status}
              </span>
            </div>

            {/* Property Info */}
            <div className="property-section">
              <h2>{t('booking.property')}</h2>
              <Link to={`/properties/${booking.property.id}`} className="property-link">
                <div className="property-mini">
                  {booking.property.images && booking.property.images.length > 0 && (
                    <PropertyThumbnail
                      src={booking.property.images[0].image_url}
                      width={200}
                      height={150}
                      alt={booking.property.title}
                    />
                  )}
                  <div>
                    <h3>{booking.property.title}</h3>
                    <p>
                      <FiMapPin size={14} />
                      {booking.property.city}, {booking.property.country}
                    </p>
                  </div>
                </div>
              </Link>
            </div>

            {/* Booking Details */}
            <div className="details-section">
              <h2>{t('booking.bookingInfo')}</h2>
              <div className="details-grid">
                <div className="detail-item">
                  <FiCalendar />
                  <div>
                    <strong>{t('booking.checkIn')}</strong>
                    <p>{new Date(booking.check_in).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="detail-item">
                  <FiCalendar />
                  <div>
                    <strong>{t('booking.checkOut')}</strong>
                    <p>{new Date(booking.check_out).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="detail-item">
                  <FiUsers />
                  <div>
                    <strong>{t('booking.guests')}</strong>
                    <p>{booking.num_guests}</p>
                  </div>
                </div>
                <div className="detail-item">
                  <span>🌙</span>
                  <div>
                    <strong>{t('booking.nights')}</strong>
                    <p>{booking.nights}</p>
                  </div>
                </div>
              </div>
            </div>

            {booking.special_requests && (
              <div className="details-section">
                <h2>{t('booking.specialRequests')}</h2>
                <p>{booking.special_requests}</p>
              </div>
            )}

            {/* Actions */}
            <div className="booking-actions">
              {isGuest && (
                <button
                  onClick={handleMessageHost}
                  className="btn btn-primary"
                >
                  <FiMessageSquare size={18} style={{ marginRight: '8px' }} />
                  {t('booking.messageHost')}
                </button>
              )}
              {isHost && (
                <button
                  onClick={handleMessageGuest}
                  className="btn btn-primary"
                >
                  <FiMessageSquare size={18} style={{ marginRight: '8px' }} />
                  {t('booking.messageGuest')}
                </button>
              )}
              {(booking.status === 'pending' || booking.status === 'confirmed') && (
                <button
                  onClick={handleCancelBooking}
                  className="btn btn-danger"
                >
                  {t('booking.cancelBooking')}
                </button>
              )}
            </div>
          </div>

          {/* Summary Sidebar */}
          <div className="booking-sidebar">
            <div className="summary-card">
              <h3>{t('booking.priceSummary')}</h3>
              <div className="summary-rows">
                <div className="summary-row">
                  <span>R$ {booking.property.price_per_night} × {booking.nights} {t('booking.nights')}</span>
                  <span>R$ {(booking.property.price_per_night * booking.nights).toFixed(2)}</span>
                </div>
                {(() => {
                  const numGuests = parseInt(booking.num_guests) || 1;
                  const baseGuests = booking.property.base_guests || 2;
                  const extraGuestPrice = parseFloat(booking.property.extra_guest_price) || 0;
                  const extraGuests = Math.max(0, numGuests - baseGuests);
                  if (extraGuests > 0 && extraGuestPrice > 0) {
                    return (
                      <div className="summary-row">
                        <span>{extraGuests} hóspede{extraGuests > 1 ? 's' : ''} adicional{extraGuests > 1 ? 'is' : ''} × {booking.nights} noite{booking.nights > 1 ? 's' : ''}</span>
                        <span>R$ {(extraGuests * extraGuestPrice * booking.nights).toFixed(2)}</span>
                      </div>
                    );
                  }
                  return null;
                })()}
                <div className="summary-row">
                  <span>{t('booking.cleaningFee')}</span>
                  <span>R$ {booking.property.cleaning_fee}</span>
                </div>
                <div className="summary-row">
                  <span>{t('booking.serviceFee')}</span>
                  <span>R$ {booking.property.service_fee}</span>
                </div>
                <div className="divider-sm"></div>
                <div className="summary-row total">
                  <strong>{t('booking.totalPrice')}</strong>
                  <strong>R$ {booking.total_price}</strong>
                </div>
              </div>
            </div>

            {booking.guest && (
              <div className="summary-card">
                <h3>{t('booking.guestInfo')}</h3>
                <p><strong>{t('booking.name')}:</strong> {booking.guest.first_name} {booking.guest.last_name}</p>
                <p><strong>{t('booking.bookedOn')}:</strong> {new Date(booking.created_at).toLocaleDateString()}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingDetail;
